import { newspinConstants } from "../constants";
import { NewsPinClass } from "../models/NewsPin";

// [{url, count}, ...] -> {url: count} for O(1) lookup per rendered news item.
function toPinCountMap(counts) {
    const map = {};
    (counts || []).forEach(c => { map[c.url] = c.count; });
    return map;
}

// Add/replace or remove just the affected item in savedNewsList, instead of
// waiting on a full getSavedNewsList() re-fetch after every pin/unpin (see
// newspin.service.js's pin()).
function applyItemDelta(list, item) {
    const withoutItem = (list || []).filter(i => i.url !== item.url);
    // Prepend, not append: matches PinRepository.getMyPinned's
    // ORDER BY pinnedat DESC (most-recently-(re-)pinned first).
    return item.pinned ? [item, ...withoutItem] : withoutItem;
}

// +1/-1 delta for the one affected url, instead of waiting on a full
// getPinCounts() re-fetch after every pin/unpin - same increment/decrement
// idiom as SpaceItem.js's setActualLumens.
function applyCountDelta(counts, item, wasPinned) {
    const current = (counts && counts[item.url]) || 0;
    let next = current;
    if (item.pinned && !wasPinned) {
        next = current + 1;
    } else if (!item.pinned && wasPinned) {
        next = current - 1 > 0 ? current - 1 : 0;
    }
    return { ...counts, [item.url]: next };
}

export function newspin(state = { loading: false, savedNewsList: [], pinCounts: {}, savedNewsListLoaded: false }, action) {
    switch (action.type) {
        case newspinConstants.NEWSPIN_SAVE_REQUEST:
            return {
                ...state,
                pin: new NewsPinClass(),
                loading: true,
                done: false
            };
        case newspinConstants.NEWSPIN_SAVE_SUCCESS: {
            const data = action.payload[0];
            if (data && data.item !== undefined) {
                // Response from pin() itself: a single item + its resulting
                // pinned flag, not the full list/counts map - apply as deltas.
                const wasPinned = (state.savedNewsList || []).some(i => i.url === data.item.url);
                return {
                    ...state,
                    pinned: data.pinned,
                    savedNewsList: applyItemDelta(state.savedNewsList, data.item),
                    pinCounts: applyCountDelta(state.pinCounts, data.item, wasPinned),
                    pin: action.payload[1],
                    loading: false,
                    done: true,
                    error: undefined
                };
            }
            // Response from getSavedNewsList()/getPinCounts() (mount-time
            // fetches in Home.js/Switch.js): full-replace as before.
            return {
                ...state,
                pinned: data.pinned !== undefined ? data.pinned : state.pinned,
                savedNewsList: data.list !== undefined ? data.list : state.savedNewsList,
                // Distinct from savedNewsList.length > 0 - a user with zero
                // saved articles still has a genuinely-completed fetch, and
                // this flag is what lets a caller (Switch.js's showSavedNews
                // toggle) tell "never fetched yet" apart from "fetched, and
                // it's just empty" without re-fetching every toggle click.
                savedNewsListLoaded: data.list !== undefined ? true : state.savedNewsListLoaded,
                pinCounts: data.counts !== undefined ? toPinCountMap(data.counts) : state.pinCounts,
                pin: action.payload[1],
                loading: false,
                done: true,
                error: undefined
            };
        }
        case newspinConstants.NEWSPIN_SAVE_FAILURE:
            return {
                ...state,
                error: action.payload[0],
                pin: action.payload[1],
                loading: false,
                done: false
            };
        case newspinConstants.NEWSPIN_SAVE_CLEAR:
            return { loading: false, savedNewsList: state.savedNewsList, pinCounts: state.pinCounts, savedNewsListLoaded: state.savedNewsListLoaded };
        default:
            return state;
    }
}
